// ============================================================================
// Interview Types
// ============================================================================

export type InterviewType = 'mock_ai' | 'coding' | 'group_discussion';

// ============================================================================
// Individual Interview Configuration (3 settings)
// ============================================================================

export interface IndividualConfig {
  difficulty: 'easy' | 'medium' | 'hard';
  experience_focus: 'varied' | 'recent_deep';
  mode: 'technical' | 'behavioral' | 'professional';
}

// ============================================================================
// Coding Interview Configuration
// ============================================================================

export interface CodingConfig {
  language: string;
  difficulty: 'easy' | 'medium' | 'hard';
  category: string;
  mode: 'timed' | 'practice';
  timed_duration_seconds?: number;
}

// ============================================================================
// Group Discussion Configuration
// ============================================================================

export interface GDConfig {
  topic_id: string;
  topic_title: string;
  category: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
}

// ============================================================================
// Union type for all configs
// ============================================================================

export type InterviewConfig = IndividualConfig | CodingConfig | GDConfig;

// ============================================================================
// Interview Node Data
// ============================================================================

export interface InterviewNodeData {
  label: string;
  interviewType: InterviewType;
  config: InterviewConfig;
  isConfigured: boolean;
}

// ============================================================================
// Interview Round Slot (for sequence builder)
// ============================================================================

export interface InterviewRoundSlot {
  id: string;
  interview: InterviewNodeData | null;
}

// ============================================================================
// Interview Pipeline (for saving/loading)
// ============================================================================

export interface InterviewPipeline {
  id: string;
  name: string;
  description?: string;
  rounds: InterviewRoundSlot[];
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Default Configurations
// ============================================================================

export const DEFAULT_INDIVIDUAL_CONFIG: IndividualConfig = {
  difficulty: 'medium',
  experience_focus: 'varied',
  mode: 'technical',
};

export const DEFAULT_CODING_CONFIG: CodingConfig = {
  language: 'python',
  difficulty: 'medium',
  category: '',
  mode: 'practice',
};

export const DEFAULT_GD_CONFIG: GDConfig = {
  topic_id: '',
  topic_title: '',
  category: '',
  difficulty: 'intermediate',
};

// ============================================================================
// Interview Type Metadata
// ============================================================================

export interface InterviewTypeMeta {
  id: InterviewType;
  title: string;
  description: string;
  icon: string;
  color: string;
}

export const INTERVIEW_TYPES: InterviewTypeMeta[] = [
  {
    id: 'mock_ai',
    title: 'Mock Interview',
    description: 'Q&A with AI interviewer',
    icon: 'triangle',
    color: '#af5ee6',
  },
  {
    id: 'coding',
    title: 'Coding',
    description: 'Live coding tasks',
    icon: 'square',
    color: '#4aadde',
  },
  {
    id: 'group_discussion',
    title: 'Group Discussion',
    description: 'Team discussion topics',
    icon: 'diamond',
    color: '#ee9c22',
  },
];

// ============================================================================
// Utility functions
// ============================================================================

export const getInterviewTypeMeta = (type: InterviewType): InterviewTypeMeta => {
  return INTERVIEW_TYPES.find((t) => t.id === type) || INTERVIEW_TYPES[0];
};

export const getDefaultConfig = (type: InterviewType): InterviewConfig => {
  switch (type) {
    case 'mock_ai':
      return { ...DEFAULT_INDIVIDUAL_CONFIG };
    case 'coding':
      return { ...DEFAULT_CODING_CONFIG };
    case 'group_discussion':
      return { ...DEFAULT_GD_CONFIG };
    default:
      return { ...DEFAULT_INDIVIDUAL_CONFIG };
  }
};
